import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaPlus, FaMinus } from "react-icons/fa";
import SectionSepratorHeader from "../../components/ui/section-seprator-header";
import Noise from "../../components/core/noise";

const faqData = [
  {
    question: "What industrial materials are utilized for signage?",
    answer:
      "We primarily engineer with architectural-grade Aluminum, Stainless Steel, and Dibond. For illuminated identifies, we utilize high-output Samsung LED modules and high-impact acrylics that are UV stabilized for Canadian climates.",
  },
  {
    question: "Does Metal FX manage structural legal requirements?",
    answer:
      "Yes. Our team handles the entire procurement process including city permit applications, structural engineering stamps, and compliance with local industrial signage bylaws.",
  },
  {
    question: "What regions do you provide on-site installation for?",
    answer:
      "We offer full-service installation crews across Northern and Southern Alberta, specifically focusing on Edmonton, Calgary, and surrounding industrial corridors. We also ship identifies for self-install throughout Canada.",
  },
  {
    question: "What is the standard engineering-to-install turnaround?",
    answer:
      "Standard identifiers typically cycle within 4-6 weeks from approved blueprint. Complex custom structures involving heavy fabrication may require 8-12 weeks depending on material supply and engineering certification.",
  },
];

const FAQItem = ({
  question,
  answer,
  isOpen,
  onClick,
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}) => {
  return (
    <div
      className={`w-full cursor-pointer border-b border-white/5 transition-all duration-500 overflow-hidden ${isOpen ? "bg-white/2" : ""}`}
    >
      <button
        onClick={onClick}
        className="w-full py-8 md:py-10 flex items-center justify-between gap-6 px-4 md:px-8 text-left hover:text-white transition-colors group"
      >
        <div className="flex items-center cursor-pointer gap-6">
          <span
            className={`text-lg md:text-2xl font-black uppercase tracking-tight transition-all ${isOpen ? "text-white" : "text-zinc-400"}`}
          >
            {question}
          </span>
        </div>
        <div
          className={`w-10 h-10 rounded-full border ${isOpen ? "border-[#4991b8] text-[#4991b8]" : "border-white/10 text-zinc-600"} flex items-center justify-center shrink-0 transition-all duration-500`}
        >
          {isOpen ? <FaMinus size={12} /> : <FaPlus size={12} />}
        </div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
          >
            <div className="px-16 md:px-32 py-6 relative">
              <Noise className="opacity-[0.03]" />
              <p className="text-zinc-500 text-lg leading-relaxed max-w-3xl">
                {answer}
              </p>
              <div className="w-12 h-1 bg-[#4991b8] mt-8 rounded-full shadow-[0_0_15px_rgba(73,145,184,0.5)]" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return (
    <section className="w-full bg-[#111212] relative">
      <SectionSepratorHeader
        title="INDUSTRIAL PROTOCOLS"
        subtitle={
          <>
            Operational{" "}
            <span className="text-[#4991b8] italic">Clarification</span>
          </>
        }
        description="Standard technical documentation and procedural answers for modern structural signage projects."
        className="mb-20"
      />

      <div className="max-w-6xl mx-auto px-4 md:px-6 relative z-10">
        <div className="border-t border-white/5">
          {faqData.map((item, idx) => (
            <FAQItem
              key={idx}
              question={item.question}
              answer={item.answer}
              isOpen={openIndex === idx}
              onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
            />
          ))}
        </div>
      </div>
      <div className="absolute top-1/2 left-0 w-64 h-64 bg-[#4991b8]/5 rounded-full blur-[100px] pointer-events-none" />
    </section>
  );
};

export default FAQ;
